import { useMemo } from 'react';

import { useAuthStore } from '@/stores/authStore';

import type { RoleType } from './roles';

/**
 * Distilled clinical role of the logged-in user. Resolves a user's flat
 * `UserRole[]` into a single privilege level for UI gating.
 *
 * Priority (highest first):
 *   admin   → super_admin / ba_super_admin / ba_admin
 *   chief   → chief_psychologist
 *   psych   → psychologist, volunteer
 *   none    → no clinical/admin role (clients and org_user land here too)
 *
 * Per a690359, "lead" is no longer a role — it's a per-project flag on
 * PractitionerAssignmentProject.isLead. Lead-flagged psychologists have
 * level === 'psych' here; lead-only UI affordances must source from
 * assignment data instead of the role.
 *
 * Volunteers map to 'psych': BE classes them as a clinical practitioner
 * with the same write surface (own clients/sessions/notes/case reports),
 * just minus engagement + case-report review. Per-permission gates (via
 * `hasPermission`) handle that subset; the level alone treats them as
 * a delivering clinician for the predicates below.
 */
export type ClinicalLevel = 'admin' | 'chief' | 'psych' | 'none';

/** Minimal client shape needed by `canBookFor`. Kept for API compat — the
 *  shape is no longer inspected (BE is authoritative). */
export interface ClientLike {
  locationId?: string | null;
}

/** Minimal practitioner shape needed by `canViewPractitioner` /
 *  `canEditPractitionerSchedule`. */
export interface PractitionerLike {
  /** Practitioner's user id (Practitioner.id === User.id in this system). */
  id?: string;
}

export interface ClinicalRoleApi {
  level: ClinicalLevel;
  /** True iff the user can book a session for this client.
   *
   *  Post-`5d141b1`, role rows no longer carry `organizationId` /
   *  `locationId`, so the FE can't compute lead/psych location scope from
   *  auth state alone. Per doc §3 the API is authoritative — and the
   *  upstream client search dropdown is already scope-filtered server-side,
   *  so any client a lead/psych can SEE in the picker is one they can BOOK
   *  for. We surface true here for any clinical/admin level and let the BE
   *  reject the rare stale-state case (e.g. assignment removed in another
   *  tab) via its own 403 toast. */
  canBookFor(client: ClientLike): boolean;
  /** True iff the user can view this practitioner's profile / availability
   *  / schedule. Same authority rationale as `canBookFor` — the
   *  `/practitioners` list is BE-scope-filtered, and self-view is always
   *  allowed. */
  canViewPractitioner(p: PractitionerLike): boolean;
  /** True iff the user can edit this practitioner's working schedule. */
  canEditPractitionerSchedule(p: PractitionerLike): boolean;
}

const ADMIN_ROLE_NAMES = new Set<RoleType>(['super_admin', 'ba_super_admin', 'ba_admin']);

/**
 * Resolve the user's clinical access level from their single role and
 * expose simple predicates for the UI to gate actions. Predicates are
 * purely level-based — fine-grained scope (org/location/project) is
 * enforced by the backend and pre-filtered into the search dropdowns
 * the user picks from. See a690359 §6.
 */
export function useClinicalRole(): ClinicalRoleApi {
  const user = useAuthStore((s) => s.user);

  return useMemo(() => {
    const role: RoleType | null = user?.role ?? null;
    const userId = user?.id ?? null;

    let level: ClinicalLevel = 'none';
    if (role !== null && ADMIN_ROLE_NAMES.has(role)) level = 'admin';
    else if (role === 'chief_psychologist') level = 'chief';
    else if (role === 'psychologist' || role === 'volunteer') level = 'psych';

    function canBookFor(_client: ClientLike): boolean {
      // Any clinical/admin level may attempt to book. The BE filters which
      // clients are bookable upstream and rejects edge cases on submit.
      return level !== 'none';
    }

    function canViewPractitioner(p: PractitionerLike): boolean {
      if (level === 'none') return false;
      // Self-view is always allowed; everything else is BE-filtered.
      if (userId && p.id === userId) return true;
      return true;
    }

    function canEditPractitionerSchedule(p: PractitionerLike): boolean {
      if (level === 'none') return false;
      if (level === 'admin' || level === 'chief') return true;
      // psych (incl. lead-flagged): only own schedule. Lead-on-other-psych
      // editing now requires assignment-derived data; defer to BE for that.
      return Boolean(userId && p.id === userId);
    }

    return {
      level,
      canBookFor,
      canViewPractitioner,
      canEditPractitionerSchedule,
    };
  }, [user]);
}
